import React from 'react';
import { captureException } from '../../utils/error/sentryClient';
import { useIntl } from 'react-intl';
import { reportError } from '../../utils/error/errorReporting';
import {
  chunkReloadRecentlyAttempted,
  isChunkLoadError,
  recoverFromChunkLoadError,
} from '../../utils/error/chunkLoadRecovery';
import { foundation } from '../../ui/tokens';
import { bg as semanticBg } from '../../ui/theme/semanticTokens';
import { ldLog } from '../../utils/ui/loginDebug';
import { dataPanel } from '../../ui/dataSurfaceStyles';
import * as p from '../../ui/primitives';

const { color, spacePx, fontSize, fontWeight, fontFamily } = foundation;

function ErrorFallback({ error }) {
  const intl = useIntl();
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
        padding: spacePx[6],
        boxSizing: 'border-box',
        backgroundColor: semanticBg.pageMuted,
        fontFamily: fontFamily.sans,
      }}
    >
      <div
        style={{
          ...dataPanel,
          // 480 = generic-modal tier (same as modalDialogPanel).
          maxWidth: 480,
          width: '100%',
          padding: spacePx[8],
          textAlign: 'center',
        }}
      >
        <h2
          style={{
            margin: `0 0 ${spacePx[3]}`,
            fontSize: fontSize.xl,
            fontWeight: fontWeight.semibold,
            color: color.neutral.text,
          }}
        >
          {intl.formatMessage({ id: 'common.somethingWentWrong', defaultMessage: 'Something went wrong' })}
        </h2>
        <p
          style={{
            margin: `0 0 ${spacePx[6]}`,
            fontSize: fontSize.sm,
            color: color.neutral.textMuted,
            lineHeight: '1.5',
          }}
        >
          {intl.formatMessage({ id: 'common.unexpectedError', defaultMessage: 'This screen ran into a problem.' })}
        </p>
        {process.env.NODE_ENV === 'development' && error && (
          <pre
            style={{
              margin: `0 0 ${spacePx[6]}`,
              padding: spacePx[4],
              textAlign: 'left',
              fontSize: '0.75rem',
              lineHeight: 1.45,
              color: color.neutral.text,
              backgroundColor: color.neutral.bgSubtle,
              borderRadius: '8px',
              overflow: 'auto',
              maxHeight: 'min(40vh, 320px)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {error?.message || String(error)}
            {error?.stack ? `\n\n${error.stack}` : ''}
          </pre>
        )}
        <button
          type="button"
          style={{ ...p.buttonPrimary }}
          onClick={() => window.location.reload()}
        >
          {intl.formatMessage({ id: 'common.tryAgain', defaultMessage: 'Try again' })}
        </button>
      </div>
    </div>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, recovering: false };
  }

  static getDerivedStateFromError(error) {
    ldLog('errorboundary', { phase: 'derived', msg: String((error && error.message) || error).slice(0, 120) });
    // A missing async chunk is recoverable (reload once). Peek the latch
    // here so the first paint is blank-while-reloading, not the crash card.
    // If the latch is already set, show the existing Try again UI — never loop.
    // Storage probe is try/catch'd inside chunkReloadRecentlyAttempted; wrap
    // anyway so a throw here cannot unmount the tree.
    try {
      if (isChunkLoadError(error) && !chunkReloadRecentlyAttempted()) {
        return { hasError: true, error, recovering: true };
      }
    } catch {
      /* treat as not recoverable */
    }
    return { hasError: true, error, recovering: false };
  }

  componentDidCatch(error, errorInfo) {
    // Always log so DevTools shows the real failure even when the UI is generic.
    console.error('ErrorBoundary caught:', error, errorInfo);
    ldLog('errorboundary', { phase: 'didcatch', msg: String((error && error.message) || error).slice(0, 120) });
    if (isChunkLoadError(error) && recoverFromChunkLoadError()) {
      return;
    }
    // Peek said we could reload, but the write failed (quota / blocked
    // storage). Drop recovering so Try again renders — never a blank screen.
    if (this.state.recovering) {
      this.setState({ recovering: false });
    }
    const component =
      errorInfo?.componentStack?.split('\n')[1]?.trim() || 'unknown';
    reportError(error, { component });
    captureException(error, { contexts: { react: { componentStack: errorInfo?.componentStack } } });
  }

  render() {
    if (this.state.hasError) {
      if (this.state.recovering) return null;
      return <ErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
